import { useState, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';

import { supplierCatalog } from '../../services/dataClient';

// ─── Constants ────────────────────────────────────────────────────────────────

const TH = {
  backgroundColor: '#eef2f6',
  fontWeight: 600,
  fontSize: '0.75rem',
  color: 'var(--grey-600)',
  textTransform: 'none',
  letterSpacing: 'normal',
  px: 2,
  py: '10px',
  borderBottom: '1px solid var(--grey-200)',
  whiteSpace: 'nowrap',
};

const TD = {
  px: 2,
  py: 0,
  border: 'none',
  fontSize: '0.8125rem',
  color: 'var(--grey-800)',
  height: 44,
};

// 3 new fake parts that will always appear as "New" in the preview
const FAKE_NEW_PARTS = [
  { partNumber: 'SFP-10G-SR',    partDescription: '10G SR SFP+ Transceiver',  quantityOnHand: 50,  unitCost: 85.00,  leadTimeDays: 3 },
  { partNumber: 'CABLE-CAT6-1M', partDescription: 'Cat6 Patch Cable 1m',       quantityOnHand: 200, unitCost: 4.50,   leadTimeDays: 2 },
  { partNumber: 'PSU-1200W',     partDescription: '1200W Platinum PSU',         quantityOnHand: 15,  unitCost: 320.00, leadTimeDays: 5 },
];

// ─── Status chip ──────────────────────────────────────────────────────────────
function StatusChip({ status }) {
  const cfg = {
    New:       { bg: '#E0F2F1', color: '#00695C' },
    Duplicate: { bg: '#ECEFF1', color: '#455A64' },
    Invalid:   { bg: '#FFEBEE', color: '#B71C1C' },
  }[status] ?? { bg: '#F5F5F5', color: '#757575' };

  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center',
      px: '8px', py: '2px', borderRadius: '10px',
      backgroundColor: cfg.bg,
    }}>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: cfg.color, lineHeight: 1 }}>
        {status}
      </Typography>
    </Box>
  );
}

// ─── Locked context field ─────────────────────────────────────────────────────
function LockedField({ label, value }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px',
        backgroundColor: '#f1f5f9', borderRadius: '6px',
        border: '1px solid var(--grey-200)', px: 1.5, py: '6px',
      }}>
        <LockOutlinedIcon sx={{ fontSize: 14, color: 'var(--grey-400)' }} />
        <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)', fontWeight: 500 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────
export default function ImportInventoryModal({ open, supplier, onClose, onImported }) {
  const [step, setStep]               = useState(1);       // 1 = upload, 2 = preview
  const [fileName, setFileName]       = useState('');
  const [previewRows, setPreviewRows] = useState([]);
  const [dragOver, setDragOver]       = useState(false);
  const fileInputRef                  = useRef(null);

  // Reset when modal opens/closes
  const handleEnter = () => {
    setStep(1);
    setFileName('');
    setPreviewRows([]);
    setDragOver(false);
  };

  // Build mock preview from this supplier's existing catalog + 3 fake new parts
  const buildPreview = useCallback((name) => {
    if (!supplier) return;

    const existingForSupplier = supplierCatalog
      .filter((e) => e.supplierId === supplier.code)
      .slice(0, 2)
      .map((e) => ({
        partNumber:      e.partNumber,
        partDescription: '',          // catalog doesn't store description
        quantityOnHand:  e.quantityOnHand,
        unitCost:        e.unitCost,
        leadTimeDays:    e.leadTimeDays,
        status:          'Duplicate',
      }));

    const newRows = FAKE_NEW_PARTS.map((p) => ({ ...p, status: 'New' }));

    setFileName(name);
    setPreviewRows([...existingForSupplier, ...newRows]);
    setStep(2);
  }, [supplier]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) buildPreview(file.name);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) buildPreview(file.name);
  }, [buildPreview]);

  const handleImport = () => {
    const newRows = previewRows.filter((r) => r.status === 'New');
    newRows.forEach((r) => {
      supplierCatalog.unshift({
        id:              `sc-import-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        supplierId:      supplier.code,
        partNumber:      r.partNumber,
        unitCost:        r.unitCost,
        leadTimeDays:    r.leadTimeDays,
        quantityOnHand:  r.quantityOnHand,
      });
    });
    onImported(supplier.name, newRows.length);
  };

  // Counts
  const newCount       = previewRows.filter((r) => r.status === 'New').length;
  const dupCount       = previewRows.filter((r) => r.status === 'Duplicate').length;
  const invalidCount   = previewRows.filter((r) => r.status === 'Invalid').length;
  const importDisabled = newCount === 0;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }}
      maxWidth="md"
      fullWidth
      TransitionProps={{ onEnter: handleEnter }}
    >
      {/* ── Title bar ── */}
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)',
        pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        Import Inventory
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '12px !important' }}>

        {/* ── Context bar ── */}
        {supplier && (
          <Box sx={{ backgroundColor: '#f8fafc', border: '1px solid var(--grey-200)',
            borderRadius: '8px', px: 2.5, py: 1.5,
          }}>
            <LockedField label="Supplier" value={supplier.name} />
          </Box>
        )}

        {/* ══════════════ STEP 1 — UPLOAD ══════════════ */}
        {step === 1 && (
          <>
            {/* Drop zone */}
            <Box
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{ border: `2px dashed ${dragOver ? '#009688' : 'var(--grey-300)'}`,
                borderRadius: '10px',
                backgroundColor: dragOver ? '#E0F2F1' : '#fafafa',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 1.5, py: 5, cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: '#009688', backgroundColor: '#f0faf9' },
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 40, color: dragOver ? '#009688' : 'var(--grey-400)' }} />
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--grey-700)' }}>
                  Drag & drop your file here
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px' }}>
                  or{' '}
                  <Typography component="span" sx={{ color: '#009688', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                    Browse
                  </Typography>
                  {' '}to choose a file
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)' }}>
                Accepted: .xlsx, .xls, .csv
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </Box>

            {/* Template download + column hint */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Typography
                onClick={(e) => e.preventDefault()}
                sx={{ fontSize: '0.8125rem', color: '#009688', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
              >
                ↓ Download Template
              </Typography>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-400)', fontWeight: 600, mb: '2px' }}>
                  Expected columns
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>
                  Part # · Description · Qty Available · Unit Cost ($) · Lead Time (days)
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* ══════════════ STEP 2 — PREVIEW ══════════════ */}
        {step === 2 && (
          <>
            {/* File name row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: 'var(--grey-400)' }} />
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)', fontWeight: 500 }}>
                {fileName}
              </Typography>
              <Typography
                onClick={() => setStep(1)}
                sx={{ fontSize: '0.8125rem', color: '#009688', cursor: 'pointer', ml: 1, '&:hover': { textDecoration: 'underline' } }}
              >
                Change file
              </Typography>
            </Box>

            {/* Summary chips */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-600)', fontWeight: 600 }}>
                Import summary:
              </Typography>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', px: '10px', py: '3px', borderRadius: '10px', backgroundColor: '#E0F2F1' }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#00695C' }}>{newCount} new</Typography>
              </Box>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', px: '10px', py: '3px', borderRadius: '10px', backgroundColor: '#ECEFF1' }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#455A64' }}>{dupCount} duplicate{dupCount !== 1 ? 's' : ''}</Typography>
              </Box>
              {invalidCount > 0 && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', px: '10px', py: '3px', borderRadius: '10px', backgroundColor: '#FFEBEE' }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#B71C1C' }}>{invalidCount} invalid</Typography>
                </Box>
              )}
            </Box>

            {/* Preview table */}
            <Box sx={{ border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Part #', 'Description', 'Qty', 'Unit Cost', 'Lead Time', 'Status'].map((h) => (
                      <TableCell key={h} sx={TH}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewRows.map((row, idx) => (
                    <TableRow
                      key={idx}
                      sx={{ height: 44,
                        '&:not(:last-child) td': { borderBottom: '1px solid var(--grey-200)' },
                        opacity: row.status === 'Duplicate' ? 0.6 : 1,
                      }}
                    >
                      <TableCell sx={{ ...TD, fontWeight: 600, color: row.status === 'New' ? '#009688' : 'var(--grey-700)' }}>
                        {row.partNumber}
                      </TableCell>
                      <TableCell sx={TD}>
                        {row.partDescription || <Typography component="span" sx={{ color: 'var(--grey-400)', fontSize: '0.8125rem' }}>—</Typography>}
                      </TableCell>
                      <TableCell sx={TD}>{row.quantityOnHand}</TableCell>
                      <TableCell sx={TD}>
                        {row.unitCost != null ? `$${row.unitCost.toFixed(2)}` : '—'}
                      </TableCell>
                      <TableCell sx={TD}>{row.leadTimeDays} days</TableCell>
                      <TableCell sx={TD}><StatusChip status={row.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {dupCount > 0 && (
              <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>
                Duplicate parts already exist in this supplier's catalog and will be skipped.
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, justifyContent: 'space-between' }}>
        {/* Left: Back (step 2 only) */}
        <Box>
          {step === 2 && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => setStep(1)}
              sx={{ textTransform: 'none', color: 'var(--grey-600)', fontWeight: 500,
                fontSize: '0.875rem',
                '&:hover': { backgroundColor: 'var(--grey-50)' },
              }}
            >
              Back
            </Button>
          )}
        </Box>

        {/* Right: Cancel + primary action */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Typography
            onClick={onClose}
            sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}
          >
            Cancel
          </Typography>

          {step === 1 ? (
            <Button
              variant="contained"
              size="small"
              onClick={() => fileInputRef.current?.click()}
              sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' },
                textTransform: 'none', fontWeight: 600, fontSize: '0.875rem',
                boxShadow: 'none', px: 2,
              }}
            >
              Browse File
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              disabled={importDisabled}
              onClick={handleImport}
              sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' },
                '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
                textTransform: 'none', fontWeight: 600, fontSize: '0.875rem',
                boxShadow: 'none', px: 2,
              }}
            >
              Import {newCount} record{newCount !== 1 ? 's' : ''}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
